/**
 * crazy-fx.js — site-wide FX engine for the SHOWCASE theme.
 *
 *  1. Scroll-reveal: fades/slides [data-reveal] elements in as they enter the viewport, and
 *     auto-tags top-level homepage sections (skipping self-animating ones).
 *  2. Parallax: translates [data-parallax] layers by their position in the viewport.
 *  3. Cursor spark trail: emits embers that follow the pointer (desktop only).
 *
 * Everything is disabled / calmed under prefers-reduced-motion. No imports (loaded as a
 * plain module from snippets/crazy-fx.liquid).
 */
const FX_REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* Scroll reveal                                                       */
/* ------------------------------------------------------------------ */
let revealIO = null;

function ensureRevealIO() {
  if (revealIO || FX_REDUCE.matches) return revealIO;
  revealIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealIO.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );
  return revealIO;
}

function observeReveal(root = document) {
  const targets = root.querySelectorAll('[data-reveal]');
  if (FX_REDUCE.matches) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = ensureRevealIO();
  targets.forEach((el) => {
    if (!el.classList.contains('is-revealed')) io.observe(el);
  });
}

function autoTagHomeSections() {
  if (!document.body.classList.contains('fx-home')) return;
  const main = document.getElementById('MainContent') || document.body;
  let sections = main.querySelectorAll(':scope > .shopify-section');
  if (!sections.length) sections = main.querySelectorAll('.shopify-section');
  sections.forEach((sec, i) => {
    if (i === 0) return; // leave the first section (hero) alone
    // Sections that animate themselves — or contain position:fixed/sticky elements that a
    // transformed ancestor would break — shouldn't get a reveal wrapper.
    if (sec.querySelector('flame-hero, burn-marquee, .flame-divider, featured-drop, promo-tab, build-reel')) return;
    if (sec.matches('[data-reveal]') || sec.querySelector('[data-reveal]')) return;
    sec.setAttribute('data-reveal', '');
    sec.setAttribute('data-reveal-soft', ''); // gentler entrance for whole sections
  });
}

/* ------------------------------------------------------------------ */
/* Parallax                                                            */
/* ------------------------------------------------------------------ */
let parallaxLayers = [];
let parallaxRaf = 0;
let parallaxBound = false;

function refreshParallax() {
  parallaxLayers = Array.from(document.querySelectorAll('[data-parallax]'));
}

function updateParallax() {
  parallaxRaf = 0;
  const vh = window.innerHeight;
  for (const layer of parallaxLayers) {
    const host = layer.parentElement || layer;
    const rect = host.getBoundingClientRect();
    if (rect.bottom < -vh || rect.top > vh * 2) continue; // far off-screen
    const speed = parseFloat(layer.dataset.parallaxSpeed || '0.3') || 0.3;
    const center = rect.top + rect.height / 2 - vh / 2;
    layer.style.transform = `translate3d(0, ${(-center * speed).toFixed(2)}px, 0)`;
  }
}

function queueParallax() {
  if (!parallaxRaf) parallaxRaf = requestAnimationFrame(updateParallax);
}

function initParallax() {
  if (FX_REDUCE.matches) return;
  refreshParallax();
  if (!parallaxLayers.length) return;
  if (!parallaxBound) {
    window.addEventListener('scroll', queueParallax, { passive: true });
    window.addEventListener('resize', queueParallax, { passive: true });
    parallaxBound = true;
  }
  updateParallax();
}

/* ------------------------------------------------------------------ */
/* Cursor spark trail                                                  */
/* ------------------------------------------------------------------ */
function initCursorTrail() {
  if (FX_REDUCE.matches) return;
  if (!window.matchMedia('(pointer: fine)').matches) return;

  const layer = document.createElement('div');
  layer.className = 'fx-trail';
  layer.setAttribute('aria-hidden', 'true');
  document.body.appendChild(layer);

  const MAX = 36;
  let last = 0;

  const spawn = (x, y) => {
    if (layer.childElementCount > MAX) layer.firstElementChild?.remove();
    const spark = document.createElement('span');
    spark.className = 'fx-spark';
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    spark.style.setProperty('--s', `${(Math.random() * 6 + 3).toFixed(1)}px`);
    spark.style.setProperty('--dx', `${(Math.random() * 28 - 14).toFixed(0)}px`);
    spark.style.setProperty('--dy', `${(Math.random() * 22 + 8).toFixed(0)}px`);
    layer.appendChild(spark);
    spark.addEventListener('animationend', () => spark.remove(), { once: true });
  };

  window.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const now = performance.now();
      if (now - last < 36) return; // ~28 sparks/sec max
      last = now;
      spawn(e.clientX, e.clientY);
    },
    { passive: true }
  );
}

/* ------------------------------------------------------------------ */
/* Spark burst on add-to-cart                                          */
/* ------------------------------------------------------------------ */
function readCartCount() {
  const el = document.querySelector('.cart-bubble__text-count');
  if (el) {
    const n = parseInt((el.textContent || '').replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function initSparkBurst() {
  if (FX_REDUCE.matches) return;
  let last = readCartCount();
  let layer = null;

  const burst = (x, y) => {
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'fx-burst';
      layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(layer);
    }
    const N = 18;
    for (let i = 0; i < N; i++) {
      const spark = document.createElement('span');
      spark.className = 'fx-burst-spark';
      const ang = (Math.PI * 2 * i) / N + Math.random() * 0.5;
      const dist = 40 + Math.random() * 55;
      spark.style.left = `${x}px`;
      spark.style.top = `${y}px`;
      spark.style.setProperty('--s', `${(Math.random() * 6 + 4).toFixed(1)}px`);
      spark.style.setProperty('--tx', `${(Math.cos(ang) * dist).toFixed(0)}px`);
      spark.style.setProperty('--ty', `${(Math.sin(ang) * dist).toFixed(0)}px`);
      layer.appendChild(spark);
      spark.addEventListener('animationend', () => spark.remove(), { once: true });
    }
  };

  // CartAddEvent fires as a bubbling 'cart:update'. Spark only when the count goes up.
  document.addEventListener('cart:update', () => {
    setTimeout(() => {
      const now = readCartCount();
      if (now > last) {
        const icon = document.getElementById('cart-icon');
        const r = icon ? icon.getBoundingClientRect() : null;
        const x = r ? r.left + r.width / 2 : window.innerWidth - 40;
        const y = r ? r.top + r.height / 2 : 40;
        burst(x, y);
      }
      last = now;
    }, 350);
  });
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */
function init() {
  autoTagHomeSections();
  observeReveal();
  initParallax();
  initCursorTrail();
  initSparkBurst();

  // Safety net: if IntersectionObserver never fires (unusual embeds/browsers), content must
  // not stay hidden. In healthy browsers something above the fold reveals immediately, so
  // this no-ops; otherwise reveal everything.
  setTimeout(() => {
    if (!document.querySelector('[data-reveal].is-revealed')) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    }
  }, 2500);
}

if (document.readyState !== 'loading') init();
else document.addEventListener('DOMContentLoaded', init);

// Theme editor: re-scan when a section is re-rendered.
document.addEventListener('shopify:section:load', (event) => {
  observeReveal(event.target);
  if (event.target.querySelector('[data-parallax]')) initParallax();
});
